import { BaseTerminalService } from './BaseTerminalService';

// Halyk POS Integration Service
class HalykPosService extends BaseTerminalService {
    constructor() {
        super();
        this.baseUrl = null;
        this.isInitialized = false;
        this.deviceInfo = null;
        this.needsRegistration = false;
        this.initializationPromise = null;
        this.isDeviceAuthorized = false;
    }

    // Проверка авторизации устройства
    checkDeviceAuthorization() {
        try {
            const deviceRegisterInfo = localStorage.getItem('device_register_info');
            if (!deviceRegisterInfo) {
                console.log('Halyk POS: Device not registered yet');
                this.needsRegistration = true;
                return false;
            }

            const deviceInfo = JSON.parse(deviceRegisterInfo);
            if (!deviceInfo || !deviceInfo.cash_register) {
                console.log('Halyk POS: Device info incomplete');
                return false;
            }

            this.isDeviceAuthorized = true;
            return true;
        } catch (error) {
            console.error('Halyk POS: Error checking device authorization:', error);
            return false;
        }
    }

    // Инициализация сервиса
    async init() {
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        if (!this.checkDeviceAuthorization()) {
            console.log('Halyk POS: Device not authorized, skipping initialization');
            return false;
        }

        this.initializationPromise = this._performInit();
        return this.initializationPromise;
    }

    async _performInit() {
        try {
            console.log('Halyk POS: Starting initialization...');

            const deviceInfo = await this.getLocalDeviceInfo();
            if (!deviceInfo || !deviceInfo.cash_register) {
                console.warn('Halyk POS: Device info or cash_register not found');
                this.needsRegistration = true;
                return false;
            }

            this.deviceInfo = deviceInfo;

            const halykConfig = this.getHalykConfig(deviceInfo);
            if (!halykConfig) {
                console.warn('Halyk POS: Halyk configuration not found');
                this.needsRegistration = true;
                return false;
            }

            this.baseUrl = `http://${halykConfig.pos_ip_address}:8080`;
            this.isInitialized = true;
            console.log(`Halyk POS: Service initialized successfully with URL: ${this.baseUrl}`);
            return true;
        } catch (error) {
            console.error('Halyk POS: Initialization error:', error);
            this.initializationPromise = null;
            return false;
        }
    }

    // Получение информации об устройстве
    async getLocalDeviceInfo() {
        try {
            const { deviceRegisterService } = await import('./posApi');
            const deviceInfo = await deviceRegisterService.getRegisterInfo();
            return deviceInfo;
        } catch (error) {
            console.error('Halyk POS: Error getting device info from API:', error);
            try {
                const deviceRegisterInfo = localStorage.getItem('device_register_info');
                if (!deviceRegisterInfo) return null;
                return JSON.parse(deviceRegisterInfo);
            } catch (storageError) {
                console.error('Halyk POS: Error parsing device info from localStorage:', storageError);
                return null;
            }
        }
    }

    // Получение конфигурации Halyk
    getHalykConfig(deviceInfo) {
        return this.getTerminalConfig(deviceInfo, 'halyk');
    }

    // Создание конфигурации Halyk POS
    createHalykConfig(posIpAddress) {
        try {
            if (!this.deviceInfo) {
                throw new Error('Device info not available');
            }

            const halykConfig = {
                pos_type: 'halyk',
                pos_ip_address: posIpAddress,
                enabled: true,
                created_at: new Date().toISOString()
            };

            const updatedDeviceInfo = {
                ...this.deviceInfo,
                payment_methods: [
                    ...(this.deviceInfo.payment_methods || []).filter(method => method.pos_type !== 'halyk'),
                    halykConfig
                ]
            };

            localStorage.setItem('device_register_info', JSON.stringify(updatedDeviceInfo));
            this.deviceInfo = updatedDeviceInfo;

            console.log('Halyk POS: Configuration created successfully');
            return halykConfig;
        } catch (error) {
            console.error('Halyk POS: Error creating Halyk config:', error);
            return null;
        }
    }


    // Fetch с таймаутом
    async fetchWithTimeout(url, options = {}, timeout = 120000) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return response;
        } catch (error) {
            clearTimeout(timeoutId);

            if (error.name === 'AbortError') {
                throw new Error('Превышен таймаут запроса (120 секунд)');
            }

            throw error;
        }
    }

    // Выполнение запроса
    async makeAuthenticatedRequest(url, options = {}) {
        if (!this.isInitialized) {
            const initialized = await this.init();
            if (!initialized) {
                throw new Error('Halyk POS service not initialized - device not authorized');
            }
        }

        const headers = {
            'Content-Type': 'application/json',
            ...options.headers
        };

        return this.fetchWithTimeout(url, {
            ...options,
            headers
        });
    }

    // Начало оплаты
    async startPayment(amount, ownCheque = false) {
        try {
            const requestBody = {
                task: 'purchase',
                data: {
                    amount: amount.toString()
                }
            };

            const response = await this.makeAuthenticatedRequest(this.baseUrl, {
                method: 'POST',
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (data.data && data.data.result === '0') {
                // Успешная оплата - имитируем processId для совместимости
                const processId = `halyk_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                return {
                    ...data.data,  // Сначала распаковываем все данные от терминала
                    success: true,
                    processId: processId,
                    status: 'success'  // Затем устанавливаем наш status (перезаписывает значение терминала)
                };
            } else {
                // Ошибка оплаты
                return {
                    success: false,
                    status: 'fail',
                    error: data.data?.msg || 'Payment failed',
                    processId: `halyk_failed_${Date.now()}`
                };
            }
        } catch (error) {
            return {
                success: false,
                status: 'fail',
                error: error.message,
                processId: `halyk_error_${Date.now()}`
            };
        }
    }

    // Начало возврата
    async startRefund(amount, transactionData, ownCheque = false) {
        try {
            if (!transactionData) {
                throw new Error('Отсутствуют данные оригинальной транзакции');
            }

            // Проверяем наличие RRN (основного идентификатора для возврата)
            const rrn = transactionData.tagRRN || transactionData.chequeInfo?.rrn || transactionData.rrn;
            if (!rrn) {
                throw new Error('Отсутствует RRN транзакции для возврата');
            }

            const requestBody = {
                task: 'refund',
                data: {
                    tagRRN: rrn,
                    method: this.getHalykRefundMethod(transactionData),
                    printFirstReceipt: false
                }
            };
            // Для GoCard (channel 6) не передаем amount
            if (transactionData.channel !== 6) {
                requestBody.data.amount = amount.toString();
            }

            // Добавляем дополнительные поля для смешанных платежей
            if (transactionData.xlsId || transactionData.goCardInfo?.xlsId) {
                requestBody.data.xlsId = (transactionData.xlsId || transactionData.goCardInfo.xlsId).toString();
            }
            if (transactionData.bonusAmount || transactionData.goCardInfo?.paidWithBonuses) {
                // Извлекаем числовое значение из строки типа "4 ₸"
                const bonusStr = transactionData.bonusAmount || transactionData.goCardInfo.paidWithBonuses;
                const bonusMatch = bonusStr.match(/(\d+)/);
                if (bonusMatch) {
                    requestBody.data.bonusAmount = bonusMatch[1];
                }
            }

            const response = await this.makeAuthenticatedRequest(this.baseUrl, {
                method: 'POST',
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (data.data && data.data.result === '0') {
                const processId = `halyk_refund_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                return {
                    ...data.data,  // Сначала распаковываем все данные от терминала
                    success: true,
                    processId: processId,
                    status: 'success'  // Затем устанавливаем наш status (перезаписывает значение терминала)
                };
            } else {
                return {
                    success: false,
                    status: 'fail',
                    error: data.data?.msg || 'Refund failed',
                    processId: `halyk_refund_failed_${Date.now()}`
                };
            }
        } catch (error) {
            return {
                success: false,
                status: 'fail',
                error: error.message,
                processId: `halyk_refund_error_${Date.now()}`
            };
        }
    }

    // Определение метода возврата для Halyk
    getHalykRefundMethod(transactionData) {
        // 0 - по карте, 1 - по QR, 2 - по GoCard
        if (transactionData.channel) {
            const channel = parseInt(transactionData.channel);
            if (channel === 7 || channel === 8 || channel === 9) {
                return 1; // QR
            }
            if (channel === 6) {
                return 2; // GoCard
            }
        }
        return 0; // По умолчанию карта
    }

    // Проверка статуса транзакции (для совместимости с Kaspi)
    async getTransactionStatus(processId) {
        // В Halyk нет отдельного метода для проверки статуса
        // Возвращаем заглушку, так как результат уже получен при startPayment/startRefund
        return {
            success: true,
            status: 'success',
            processId: processId
        };
    }

    // Очистка ресурсов
    destroy() {
        this.initializationPromise = null;
        this.isInitialized = false;
    }

    // Сброс сервиса
    reset() {
        this.destroy();
        this.baseUrl = null;
        this.deviceInfo = null;
        this.needsRegistration = false;
        this.isDeviceAuthorized = false;
    }
}

// Глобальный экземпляр сервиса
const halykPOSService = new HalykPosService();

export { halykPOSService };